<template>
  <div
    class="user-icon-wrapper"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <AppIcon
      name="user"
      size="body-xl"
      class-name="color-lighter"
      :clickable="true"
    />
    <div v-if="showOptions" class="user-menu">
      <div class="user-info">
        <div class="user-display-name">{{ products.displayName }}</div>
        <div class="user-name">{{ products.userName }}</div>
      </div>
      <FormButton
        :label="t('user_settings.logout')"
        type="primary"
        class-name="logout-button"
        :on-click="handleLogout"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import FormButton from '@/components/common/button/FormButton.vue';
import { spainAuthenticationEndpoints } from '@/core/endpoints';
import EventConstants from '@/core/eventConstants';
import { AuthMethods } from '@/models/auth-methods';
import authService from '@/services/AuthService';
import navigationService from '@/services/NavigationService';
import { useAuthStore } from '@/stores/authStore';
import { AuthUtils } from '@/utils/auth.utils';
import eventEmitter from '@/utils/eventEmitter';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { authMethod, products } = useAuthStore();

const showOptions = ref(false);
let hideTimeout: NodeJS.Timeout | null = null;

const handleMouseEnter = () => {
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
  showOptions.value = true;
};

const handleMouseLeave = () => {
  hideTimeout = setTimeout(() => {
    showOptions.value = false;
  }, 150);
};

const logout = () => {
  eventEmitter.emit(EventConstants.LOGOUT_EVENT);
  navigationService.navigateTo({
    name: AuthUtils.getLoginRouteName(authMethod)
  });
};

const showSpainLogout = async () => {
  const backendLoginUrl = `${process.env.VUE_APP_API_URL}${spainAuthenticationEndpoints.LOGOUT}`;

  Office.context.ui.displayDialogAsync(
    backendLoginUrl,
    { height: 80, width: 80, promptBeforeOpen: false },
    (asyncResult) => {
      const dialog = asyncResult.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if ('message' in arg && arg.message.includes('logged-out')) {
          dialog.close();
          logout();
        }
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        dialog.close();
        logout();
      });
    }
  );
};

const handleLogout = async () => {
  showOptions.value = false;

  if (authMethod === AuthMethods.Spain) {
    showSpainLogout();
  } else {
    authService.franceLogout();
    logout();
  }
};
</script>
<style scoped lang="scss">
.user-icon-wrapper {
  position: relative;
  display: inline-block;
}

.user-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: $color-neutral-0;
  border: 1px solid $color-neutral-200;
  border-radius: $radius-3;
  box-shadow: $floating-card-shadow;
  z-index: 1000;
  min-width: 12.5rem;
  padding: $space-4;
  margin-top: $space-1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-3;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-1;
  text-align: center;
  width: 100%;
}

.user-display-name {
  font-size: $body-m-font-size;
  line-height: $body-m-line-height;
  font-weight: $fw-medium;
  color: $color-text-dark;
}

.user-name {
  font-size: $body-s-font-size;
  line-height: $body-s-line-height;
  font-weight: $fw-regular;
  color: $color-text-light;
}

.logout-button {
  width: 100%;
}

@media (min-height: $small-screen-height) {
  .user-menu {
    padding: $space-5;
    gap: $space-4;
    min-width: 13.75rem;
  }

  .user-info {
    gap: $space-2;
  }

  .user-display-name {
    font-size: $body-l-font-size;
    line-height: $body-l-line-height;
  }

  .user-name {
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
  }
}
</style>
